import { Action as BaseAction } from 'redux'
import { ThunkAction } from 'redux-thunk'

import gateway, { SIMPLE_EMAIL_REGEX } from '../gateway/gateway'
import { isRequestCancelled } from '../services/request'
import { Filters, Type } from '../types/filters'
import { Action as ActionType, Dispatch } from '../types/reducer'
import { SnippetsState, GetState, RootState } from '../types/state'
import { Snippet, Query } from '../types/snippet'
import { User, Account } from '../types/user'
import { ExtraArgument } from '../types/thunk'
import { InterlocutorDetails } from '../types/person'
import { PrivacySetting } from '../types/group'
import { visibleGroupsSelector } from './groups'
import { notify } from '../redux/notifications'
import { messages } from './messages'
import { getPersonByEmail } from '../services/people'
import { captureException } from '../sentry-config'

// Actions
export const ASSIGN_SNIPPETS_LIST = 'snippets/ASSIGN_SNIPPETS_LIST'
export const MARK_SNIPPETS_LOADING = 'snippets/MARK_SNIPPETS_LOADING'
export const TOGGLE_SNIPPET_OVERLAY = 'snippets/TOGGLE_SNIPPET_OVERLAY'
export const FILTER_SNIPPETS_LIST = 'snippets/FILTER_SNIPPETS_LIST'
export const TOGGLE_DIALOG = 'snippets/TOGGLE_DIALOG'
export const SET_MISSED_TAGS = 'snippets/SET_MISSED_TAGS'

type AssignSnippetsListAction = ActionType<typeof ASSIGN_SNIPPETS_LIST, Snippet[]>
type MarkSnippetsLoadingAction = ActionType<typeof MARK_SNIPPETS_LOADING, boolean>
type ToggleSnippetOverlayAction = ActionType<typeof TOGGLE_SNIPPET_OVERLAY, boolean>
type FilterSnippetsListAction = ActionType<typeof FILTER_SNIPPETS_LIST, string>
type ToggleDialogAction = ActionType<typeof TOGGLE_DIALOG, boolean>
type SetMissedTagsAction = ActionType<typeof SET_MISSED_TAGS, string[]>
type UnknownThunkAction = ThunkAction<unknown, RootState, ExtraArgument, BaseAction>

type Action =
  | AssignSnippetsListAction
  | MarkSnippetsLoadingAction
  | ToggleSnippetOverlayAction
  | FilterSnippetsListAction
  | ToggleDialogAction
  | SetMissedTagsAction

// Initial state and reducer
const initialState: SnippetsState = {
  snippetsList: [],
  isOverlayVisible: false,
  inProgress: true,
  query: '',
  isWarningDialogShow: false,
  missedTags: [],
}

export default function snippets(state: SnippetsState = initialState, { type, payload }: Action) {
  switch (type) {
    case ASSIGN_SNIPPETS_LIST:
      return {
        ...state,
        snippetsList: payload,
        query: '',
      }
    case MARK_SNIPPETS_LOADING:
      return {
        ...state,
        inProgress: payload,
      }
    case TOGGLE_SNIPPET_OVERLAY:
      return {
        ...state,
        isOverlayVisible: payload,
      }

    case FILTER_SNIPPETS_LIST:
      return {
        ...state,
        query: payload,
      }
    case SET_MISSED_TAGS:
      return {
        ...state,
        missedTags: payload,
      }
    case TOGGLE_DIALOG:
      return {
        ...state,
        isWarningDialogShow: payload,
      }
    default:
      return state
  }
}

// Action creators
export function assignSnippetsList(snippetsList: Snippet[]): AssignSnippetsListAction {
  return {
    type: ASSIGN_SNIPPETS_LIST,
    payload: snippetsList,
  }
}

export function assignInProgress(inProgress: boolean): MarkSnippetsLoadingAction {
  return {
    type: MARK_SNIPPETS_LOADING,
    payload: inProgress,
  }
}

export function toggleSnippetInsertOverlay(isOverlayVisible: boolean): ToggleSnippetOverlayAction {
  return {
    type: TOGGLE_SNIPPET_OVERLAY,
    payload: isOverlayVisible,
  }
}

export function filterSnippetsList(query: string): FilterSnippetsListAction {
  return {
    type: FILTER_SNIPPETS_LIST,
    payload: query,
  }
}

export function toggleSnippetDialog(isWarningDialogShow: boolean): ToggleDialogAction {
  return {
    type: TOGGLE_DIALOG,
    payload: isWarningDialogShow,
  }
}

export function setMissedTags(missedTags: string[]): SetMissedTagsAction {
  return {
    type: SET_MISSED_TAGS,
    payload: missedTags,
  }
}

function teamAccounts(user: User) {
  const hasTeamPermission = user.account?.permission_names.includes('view_team_snippets')
  return hasTeamPermission ? user.account?.team.accounts : []
}

export const byAuthorized = (currentUser: User) => (account: Account) => {
  const currentGroupId = currentUser.account?.group_id
  const currentAccountId = currentUser.account?.id
  const isInGroup = Boolean(currentGroupId)
  const isGroupVisible = currentUser.team.group_privacy_setting === 'group_public'
  const isAllVisible = currentUser.team.group_privacy_setting === 'all_public'
  const isTeamAdmin = currentUser.account?.team_admin

  // show everything for the team admin
  if (isTeamAdmin) {
    return true
  }

  // filter out inactive accounts for other roles
  if (!account.active) {
    return false
  }

  // if groups are public show every other account
  if (isAllVisible) {
    return true
  }

  // if groups are private and the user is in a group
  if (isGroupVisible && isInGroup) {
    // return accounts from the same group
    return currentGroupId === account.group_id
  } else {
    // return user's own account
    return currentAccountId === account.id
  }
}

function guidsList(user: User) {
  const accounts = teamAccounts(user)
  return accounts?.filter(byAuthorized(user)).map(account => account.user.user_guid) || []
}

export const getSnippetsList = (user: User) => (
  dispatch: Dispatch<MarkSnippetsLoadingAction | AssignSnippetsListAction, void>
) => {
  if (user === null) {
    throw Error('Current user is null ')
  }
  const account = user.account
  if (account === null) {
    throw Error('Account id is missing')
  }
  const filterOwners = {
    personalUuids: [user.user_guid],
    teamUuids: guidsList(user),
  }
  return gateway.snippets
    .getAllSnippets(filterOwners)
    .then(data => {
      dispatch(assignSnippetsList(data))
      dispatch(assignInProgress(false))
    })
    .catch(e => {
      dispatch(assignInProgress(false))
      throw e
    })
}

export const getSnippets = (type: Type, filters: Filters) => async (
  dispatch: Dispatch<MarkSnippetsLoadingAction | AssignSnippetsListAction, void>,
  getState: GetState
) => {
  const state = getState()
  if (!state) return
  const { account, team } = state.currentUser
  const restrictedByGroup =
    !account.team_admin && team.group_privacy_setting === PrivacySetting.GROUP_PUBLIC
  const query: Query = { content: filters.query?.trim() }

  if (type === Type.TEAM) {
    if (restrictedByGroup && filters.groups.length === 0) {
      query.filter_groups = visibleGroupsSelector(state).map(({ id }) => id)
    } else {
      query.filter_groups = filters.groups.map(({ id }) => id)
    }

    if (restrictedByGroup && !account.group_id) {
      query.filter_owners = []
    }
  }

  dispatch(assignInProgress(true))

  try {
    const snippets = await gateway.snippets.getSnippetsByType(type, query)

    dispatch(assignSnippetsList(snippets))

    return snippets
  } catch (e) {
    if (isRequestCancelled(e)) {
      throw e
    }

    captureException(e)
  } finally {
    dispatch(assignInProgress(false))
  }

  return []
}

const getPopulatedSnippet = (email: string, body: string) => {
  const payload = {
    to: email,
    body,
    subject: '',
  }

  return gateway.emails.create(payload).catch((e: Error) => {
    captureException(e)

    return payload
  })
}

const missingSnippetTags = (body: string) => {
  const tagRegex = /{{[^}]+}}/g
  const missingBodyTags = body.match(tagRegex) || []
  return Array.from(missingBodyTags)
}

const checkMissingTags = (body: string) => (
  dispatch: Dispatch<SetMissedTagsAction | ToggleDialogAction, void>
) => {
  const missingTags = missingSnippetTags(body)

  if (missingTags.length > 0) {
    dispatch(setMissedTags(missingTags))
    dispatch(toggleSnippetDialog(true))
  }
}

export const getRenderedSnippet = (email: string, selectedSnippet: Snippet, currentUser?: User) => (
  dispatch: Dispatch<UnknownThunkAction, void>
) =>
  getPersonByEmail(email, currentUser)
    .then(response => {
      if (Array.isArray(response) ? response.length === 0 : !response) {
        return { body: selectedSnippet.body }
      }
      return getPopulatedSnippet(email, selectedSnippet.body)
    })
    .catch(e => {
      dispatch(
        notify({
          type: 'error',
          message: messages.textNotifyNoAccessToRecipients,
        })
      )
      throw e
    })
    .then(json => {
      if (!json) {
        dispatch(
          notify({
            type: 'error',
            message: messages.textNotifyRecipientDNC,
          })
        )
        return
      }
      return json.body
    })
    .then(body => {
      dispatch(checkMissingTags(body))
      return body
    })

export const getSnippetBody = (snippetId: number) => (
  dispatch: Dispatch<UnknownThunkAction, void>,
  getState: GetState,
  { office }: ExtraArgument
) => {
  const selectedSnippet = getState()?.snippets.snippetsList.find(
    snippet => snippet.id === snippetId
  )

  if (!selectedSnippet) {
    captureException(new Error(`Failed to find snippet ${snippetId}`))

    return Promise.resolve('')
  }

  return office.item?.getInterlocutorDetails().then((interlocutorDetails: InterlocutorDetails) => {
    const email = interlocutorDetails.emailAddress
    if (email == null || !SIMPLE_EMAIL_REGEX.test(email)) {
      return selectedSnippet.body
    } else {
      return dispatch(getRenderedSnippet(email, selectedSnippet, getState()?.currentUser))
    }
  })
}

export const updateCounts = (snippetId: number) => (): Promise<unknown> =>
  gateway.snippets.updateCounts(snippetId).catch(captureException)

export const snippetsSelector = (state: RootState): Snippet[] => state.snippets.snippetsList
